import { createContext, ReactNode, useCallback, useState } from 'react';

type GlobalModalsState = {
  deleteDeviceModal: {
    opened: boolean;
    deviceId?: number;
  };
  deleteInteractionModal: {
    opened: boolean;
    interactionId?: number;
  };
  deleteInteractionDictionaryModal: {
    opened: boolean;
    interactionDictionaryId?: number;
  };
  deleteThemeModal: {
    opened: boolean;
    themeId?: number;
  };
  deleteBrandingModal: {
    opened: boolean;
    brandingId?: number;
  };
  deleteCampaignModal: {
    opened: boolean;
    campaignId?: number;
  };
  deleteCampaignSlotModal: {
    opened: boolean;
    slotId?: number;
  };
  restartDeviceModal: {
    opened: boolean;
    deviceId?: number;
  };
  loadingModal: { opened: boolean; title: string; description: string };
};

type GlobalModalsActions = {
  openDeleteDeviceModal: (deviceId: number) => void;
  closeDeleteDeviceModal: () => void;

  openDeleteInteractionModal: (interactionId: number) => void;
  closeDeleteInteractionModal: () => void;

  openDeleteInteractionDictionaryModal: (
    interactionDictionaryId: number
  ) => void;
  closeDeleteInteractionDictionaryModal: () => void;

  openDeleteThemeModal: (themeId: number) => void;
  closeDeleteThemeModal: () => void;

  openDeleteBrandingModal: (brandingId: number) => void;
  closeDeleteBrandingModal: () => void;

  openDeleteCampaignModal: (campaignId: number) => void;
  closeDeleteCampaignModal: () => void;

  openDeleteCampaignSlotModal: (slotId: number) => void;
  closeDeleteCampaignSlotModal: () => void;

  openRestartDeviceModal: (deviceId: number) => void;
  closeRestartDeviceModal: () => void;

  openLoadingModal: (title: string, description: string) => void;
  closeLoadingModal: () => void;
};

type GlobalModalsContextType = GlobalModalsState & GlobalModalsActions;

const GlobalModalsContext = createContext<GlobalModalsContextType | null>(null);

type GlobalModalsProviderProps = {
  children: ReactNode;
};

export const GlobalModalsProvider = ({
  children,
}: GlobalModalsProviderProps) => {
  const [state, setState] = useState<GlobalModalsState>({
    deleteDeviceModal: { opened: false },
    deleteInteractionDictionaryModal: { opened: false },
    deleteInteractionModal: { opened: false },
    deleteThemeModal: { opened: false },
    deleteBrandingModal: { opened: false },
    deleteCampaignModal: { opened: false },
    deleteCampaignSlotModal: { opened: false },
    loadingModal: { description: '', opened: false, title: '' },
    restartDeviceModal: { opened: false },
  });

  const openDeleteDeviceModal = (deviceId: number) => {
    setState((prev) => ({
      ...prev,
      deleteDeviceModal: { deviceId, opened: true },
    }));
  };

  const closeDeleteDeviceModal = () => {
    setState((prev) => ({
      ...prev,
      deleteDeviceModal: { opened: false },
    }));
  };

  const openDeleteInteractionModal = (interactionId: number) => {
    setState((prev) => ({
      ...prev,
      deleteInteractionModal: {
        interactionId,
        opened: true,
      },
    }));
  };

  const closeDeleteInteractionModal = () => {
    setState((prev) => ({
      ...prev,
      deleteInteractionModal: { opened: false },
    }));
  };

  const openDeleteInteractionDictionaryModal = (
    interactionDictionaryId: number
  ) => {
    setState((prev) => ({
      ...prev,
      deleteInteractionDictionaryModal: {
        interactionDictionaryId,
        opened: true,
      },
    }));
  };

  const closeDeleteInteractionDictionaryModal = () => {
    setState((prev) => ({
      ...prev,
      deleteInteractionDictionaryModal: { opened: false },
    }));
  };

  const openDeleteThemeModal = (themeId: number) => {
    setState((prev) => ({
      ...prev,
      deleteThemeModal: { themeId, opened: true },
    }));
  };

  const closeDeleteThemeModal = () => {
    setState((prev) => ({
      ...prev,
      deleteThemeModal: { opened: false },
    }));
  };

  const openDeleteBrandingModal = (brandingId: number) => {
    setState((prev) => ({
      ...prev,
      deleteBrandingModal: { brandingId, opened: true },
    }));
  };

  const closeDeleteBrandingModal = () => {
    setState((prev) => ({
      ...prev,
      deleteBrandingModal: { opened: false },
    }));
  };

  const openDeleteCampaignModal = (campaignId: number) => {
    setState((prev) => ({
      ...prev,
      deleteCampaignModal: { campaignId, opened: true },
    }));
  };

  const closeDeleteCampaignModal = () => {
    setState((prev) => ({
      ...prev,
      deleteCampaignModal: { opened: false },
    }));
  };

  const openDeleteCampaignSlotModal = (slotId: number) => {
    setState((prev) => ({
      ...prev,
      deleteCampaignSlotModal: { slotId, opened: true },
    }));
  };

  const closeDeleteCampaignSlotModal = () => {
    setState((prev) => ({
      ...prev,
      deleteCampaignSlotModal: { opened: false },
    }));
  };

  const openRestartDeviceModal = (deviceId: number) => {
    setState((prev) => ({
      ...prev,
      restartDeviceModal: { deviceId, opened: true },
    }));
  };

  const closeRestartDeviceModal = () => {
    setState((prev) => ({
      ...prev,
      restartDeviceModal: { opened: false },
    }));
  };

  const openLoadingModal = useCallback((title: string, description: string) => {
    setState((prev) => ({
      ...prev,
      loadingModal: { description, opened: true, title },
    }));
  }, []);

  const closeLoadingModal = useCallback(() => {
    setTimeout(() => {
      setState((prev) => ({
        ...prev,
        loadingModal: { description: '', opened: false, title: '' },
      }));
    }, 300);
  }, []);

  const contextValue: GlobalModalsContextType = {
    ...state,
    closeDeleteDeviceModal,
    closeDeleteInteractionDictionaryModal,
    closeDeleteInteractionModal,
    closeDeleteThemeModal,
    closeDeleteBrandingModal,
    closeDeleteCampaignModal,
    closeDeleteCampaignSlotModal,
    closeLoadingModal,
    closeRestartDeviceModal,
    openDeleteDeviceModal,
    openDeleteInteractionDictionaryModal,
    openDeleteInteractionModal,
    openDeleteThemeModal,
    openDeleteBrandingModal,
    openDeleteCampaignModal,
    openDeleteCampaignSlotModal,
    openLoadingModal,
    openRestartDeviceModal,
  };

  return (
    <GlobalModalsContext.Provider value={contextValue}>
      {children}
    </GlobalModalsContext.Provider>
  );
};

export { GlobalModalsContext };
